import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SearchWithSuggestions from '../common/SearchWithSuggestions';

const { FiMenu, FiX, FiHome, FiList, FiBarChart3, FiInfo, FiMail } = FiIcons;

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navigationItems = [
    { name: 'Home', path: '/', icon: FiHome },
    { name: 'All Pokemon', path: '/pokemon', icon: FiList },
    { name: 'Types', path: '/types', icon: FiBarChart3 },
    { name: 'Compare', path: '/compare', icon: FiBarChart3 },
    { name: 'About', path: '/about', icon: FiInfo },
    { name: 'Contact', path: '/contact', icon: FiMail }
  ];

  return (
    <header className="bg-white shadow-lg border-b-4 border-blue-500 sticky top-0 z-40" role="banner">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center space-x-2 text-xl font-bold text-blue-600 hover:text-blue-700 transition-colors focus:ring-2 focus:ring-blue-500 rounded-md px-2 py-1"
            aria-label="Pokemon Stats Database - Home"
          >
            <span className="text-2xl">⚡</span>
            <span>Pokemon Stats DB</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6" role="navigation" aria-label="Main navigation">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors focus:ring-2 focus:ring-blue-500 ${
                  location.pathname === item.path
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                }`}
                aria-current={location.pathname === item.path ? 'page' : undefined}
              >
                <SafeIcon icon={item.icon} className="w-4 h-4" />
                <span>{item.name}</span>
              </Link>
            ))}
          </nav>

          {/* Desktop Search Bar */}
          <div className="hidden md:block w-72">
            <SearchWithSuggestions className="flex items-center" idPrefix="desktop-search" />
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-md text-gray-700 hover:text-blue-600 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500"
            aria-label="Toggle mobile menu"
            aria-expanded={isMenuOpen}
          >
            <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
          </button>
        </div>

        {/* Mobile Menu */}
        <motion.div
          initial={false}
          animate={{ height: isMenuOpen ? 'auto' : 0, opacity: isMenuOpen ? 1 : 0 }}
          transition={{ duration: 0.3 }}
          className="md:hidden overflow-hidden"
        >
          <div className="py-4 space-y-2">
            {/* Mobile Search */}
            <div className="mb-4">
              <SearchWithSuggestions 
                className="flex items-center space-x-2" 
                onSearchSubmit={() => setIsMenuOpen(false)}
                idPrefix="mobile-search"
              />
            </div>

            {/* Mobile Navigation */}
            <nav className="space-y-1" role="navigation" aria-label="Mobile navigation">
              {navigationItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors focus:ring-2 focus:ring-blue-500 ${
                    location.pathname === item.path
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                  }`}
                  aria-current={location.pathname === item.path ? 'page' : undefined}
                >
                  <SafeIcon icon={item.icon} className="w-4 h-4" />
                  <span>{item.name}</span>
                </Link>
              ))}
            </nav>
          </div>
        </motion.div>
      </div>
    </header>
  );
}

export default Header;